import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import OverLayLoader from '../../components/OverLayLoader';
import AddReplacementForm from '../../components/AddReplacementForm';
import SubviewContainer from '../../components/SubviewContainer';

import { getLanguages, createReplacement } from '../../services';
import { createErrorNotification } from '../../utils/createErrorNotification';
import useTitle from '../../hooks/useTitle';
import PATH from '../../helpers/routes.json';

const AddReplacementView = () => {
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { language } = useParams();
  const { state } = useLocation();

  const { t } = useTranslation(['services', 'promptbar']);

  const title = t(`promptbar:addReplacement`);
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  const gotoPath = state
    ? `${state.pathname + state.search}`
    : PATH.dictionaries.primary;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const langs = await getLanguages();

        if (langs) {
          setCurrentLanguage(langs.data.find(lng => lng.name === language));
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [language]);

  const formSubmitHandler = async data => {
    try {
      setIsLoading(true);
      const result = await createReplacement(data);
      if (result) {
        navigate(gotoPath);
      }
    } catch (error) {
      if (error?.response?.status === 422 && error?.response?.data?.detail) {
        createErrorNotification(error.response.data.detail);
      } else {
        createErrorNotification();
      }

      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelHandler = () => {
    navigate(gotoPath);
  };

  return (
    <SubviewContainer title={title}>
      {isLoading && <OverLayLoader />}
      {currentLanguage && (
        <Box sx={{ padding: '3rem 2rem 2rem' }}>
          <AddReplacementForm
            currentLanguage={currentLanguage}
            onCancel={cancelHandler}
            onSubmit={formSubmitHandler}
          />
        </Box>
      )}
    </SubviewContainer>
  );
};

export default AddReplacementView;
